'use client';

import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';
import { AttributeDefinition, Component } from '@/lib/decorators';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { Region, type ComponentType } from '@/components/region';
import { Accordion } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useProduct } from '@/providers/product-context';

const ACCORDION_ITEMS_REGION_ID = 'accordionItems';
const HOW_IT_WORKS_REGION_ID = 'howItWorks';
export const GIFT_CARD_DETAILS_TAB_ID = 'details';
export const GIFT_CARD_HOW_IT_WORKS_TAB_ID = 'howItWorks';

const TAB_TRIGGER_CLASSNAME =
    'label-m shrink-0 bg-sand-95 text-neutral-10 rounded-full hover:bg-neutral-10 hover:text-neutral-white data-[state=active]:bg-neutral-10 data-[state=active]:text-neutral-white md:flex-none md:w-fit';

/* v8 ignore start - do not test decorators in unit tests, decorator functionality is tested separately */
@Component('giftCardProductTabs', {
    name: 'Gift Card | Product tabs',
    description:
        'Gift card PDP tabs: Dettagli with product description and editorial accordion, plus optional Come funziona section.',
    group: 'content',
})
@RegionDefinition([
    {
        id: ACCORDION_ITEMS_REGION_ID,
        name: 'Accordion items',
        description: 'Accordion panels shown in the Dettagli tab (e.g. shipping, usage).',
        maxComponents: 6,
        componentTypeInclusions: ['content.giftCardAccordionItem'],
    },
    {
        id: HOW_IT_WORKS_REGION_ID,
        name: 'How it works',
        description: 'Optional Come funziona tab content.',
        maxComponents: 1,
        componentTypeInclusions: ['content.giftCardHowItWorks'],
    },
])
export class GiftCardProductTabsMetadata {
    @AttributeDefinition({
        id: 'detailsTabLabel',
        name: 'Details tab label',
        description: 'Label for the Dettagli tab pill. Falls back to the default translation when empty.',
        type: 'string',
        required: false,
        searching: {
            searchable: true,
            refinable: true,
            boostFactor: 2,
            sortable: true,
        },
    })
    detailsTabLabel?: string;
}
/* v8 ignore stop */

export interface GiftCardProductTabsProps {
    detailsTabLabel?: string;
    component?: ComponentType;
    designMetadata?: ComponentDesignMetadata;
    data?: unknown;
    regionId?: string;
}

function getHowItWorksTabLabel(component?: ComponentType, fallbackLabel?: string): string | undefined {
    const howItWorksComponent = component?.regions?.find((region) => region.id === HOW_IT_WORKS_REGION_ID)
        ?.components?.[0];
    const tabLabel = (howItWorksComponent?.data as { tabLabel?: string } | undefined)?.tabLabel?.trim();
    return tabLabel || fallbackLabel;
}

function hasHowItWorksTab(component?: ComponentType): boolean {
    return Boolean(component?.regions?.find((region) => region.id === HOW_IT_WORKS_REGION_ID)?.components?.length);
}

export function fallback(): ReactElement {
    return (
        <div
            className="gap-8 max-w-screen-2xl mx-auto w-full"
            aria-hidden
            data-testid="gift-card-product-tabs-fallback">
            <div className="flex gap-3 max-w-full overflow-x-auto scrollbar-hide">
                <Skeleton className="h-9 w-28 shrink-0 rounded-full" />
                <Skeleton className="h-9 w-36 shrink-0 rounded-full" />
            </div>
            <div className="grid grid-cols-12 gap-4 pt-6">
                <Skeleton className="col-span-12 lg:col-span-7 h-32" />
                <Skeleton className="col-span-12 lg:col-span-4 lg:col-end-13 h-40" />
            </div>
        </div>
    );
}

export default function GiftCardProductTabs({
    detailsTabLabel,
    component,
}: GiftCardProductTabsProps): ReactElement | null {
    const { t } = useTranslation('product');
    const product = useProduct();
    const productDescription = product?.longDescription;
    const showHowItWorks = hasHowItWorksTab(component);

    const sections = useMemo(() => {
        const detailsLabel = detailsTabLabel?.trim() || t('tabs.details.title');
        const howItWorksLabel = getHowItWorksTabLabel(component, t('tabs.howItWorks.title'));

        return [
            { id: GIFT_CARD_DETAILS_TAB_ID, label: detailsLabel },
            ...(showHowItWorks && howItWorksLabel
                ? [{ id: GIFT_CARD_HOW_IT_WORKS_TAB_ID, label: howItWorksLabel }]
                : []),
        ];
    }, [component, detailsTabLabel, showHowItWorks, t]);

    if (!component) {
        return null;
    }

    return (
        <Tabs
            defaultValue={GIFT_CARD_DETAILS_TAB_ID}
            className="gap-8 max-w-screen-2xl mx-auto w-full min-w-0"
            data-testid="gift-card-product-tabs">
            <TabsList
                scrollActiveTabOnMobile
                className="flex w-full max-w-full min-w-0 bg-transparent gap-3 overflow-x-auto scrollbar-hide overflow-y-hidden justify-start">
                {sections.map((section) => (
                    <TabsTrigger key={section.id} value={section.id} className={TAB_TRIGGER_CLASSNAME}>
                        {section.label}
                    </TabsTrigger>
                ))}
            </TabsList>
            <TabsContent value={GIFT_CARD_DETAILS_TAB_ID} className="w-full">
                <div className="grid grid-cols-12 gap-4 lg:justify-between">
                    <div className="col-span-12 lg:col-span-7">
                        {productDescription ? (
                            <div
                                className="prose prose-sm max-w-none marker:text-black [&_ul]:list-disc [&_ul]:pl-5 body-copy-l text-neutral-10"
                                data-testid="gift-card-details-description"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: productDescription }}
                            />
                        ) : null}
                    </div>
                    <div className="col-span-12 lg:col-span-4 lg:col-end-13">
                        <Accordion type="multiple" className="w-full" data-testid="gift-card-details-accordion">
                            <Region component={component} regionId={ACCORDION_ITEMS_REGION_ID} className="contents" />
                        </Accordion>
                    </div>
                </div>
            </TabsContent>
            {showHowItWorks ? (
                <TabsContent value={GIFT_CARD_HOW_IT_WORKS_TAB_ID} className="w-full">
                    <Region component={component} regionId={HOW_IT_WORKS_REGION_ID} />
                </TabsContent>
            ) : null}
        </Tabs>
    );
}
